import { useMemo } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { UPLOADS_BASE } from "../constants/api";
import {
  filterCitiesForParent,
  filterCountries,
  findLocationTypeId,
} from "../../../utils/locationHelpers";
import { YesNoRadioInline, normalizeEstablishmentTypes, filterEstablishmentSubTypes } from "./index";

const EstSectionCard = ({ title, sectionNumber, children }) => (
  <div className="em-section-card">
    {title && (
      <h4 className="em-section-title">
        {sectionNumber != null && <span className="em-section-num">{sectionNumber}. </span>}
        {title}
      </h4>
    )}
    {children}
  </div>
);

const EstPhoneList = ({ formData, onChange }) => {
  const phones = (() => {
    if (Array.isArray(formData.phone_numbers) && formData.phone_numbers.length > 0) {
      return formData.phone_numbers;
    }
    const initial = [formData.phone_number_1 || "", formData.phone_number_2 || ""].filter(
      (p, i) => i === 0 || p
    );
    return initial.length ? initial : [""];
  })();

  const syncPhones = (next) => {
    onChange("phone_numbers", next);
    onChange("phone_number_1", next[0] || "");
    onChange("phone_number_2", next[1] || "");
  };

  return (
    <div className="em-phone-list">
      {phones.map((phone, idx) => (
        <div key={idx} className="em-phone-row">
          <Form.Control
            type="text"
            placeholder="Phone number"
            value={phone}
            onChange={(e) => {
              const next = [...phones];
              next[idx] = e.target.value;
              syncPhones(next);
            }}
          />
          {phones.length > 1 && (
            <button
              type="button"
              className="em-btn-remove"
              onClick={() => syncPhones(phones.filter((_, i) => i !== idx))}
              aria-label="Remove phone"
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="em-btn-add"
        onClick={() => syncPhones([...phones, ""])}
      >
        + Add more
      </button>
    </div>
  );
};

const EstablishmentProfileForm = ({
  basicOnly = false,
  lockBasicInfo = false,
  lockLocationFields = false,
  formData, onChange, locationTypes, locations, accreditations, establishments, estTypes,
  establishmentSubTypes = [],
  departments = [],
  specializationTypes = [],
}) => {
  // Derive location_type ids from locationTypes (supports labels like "State/ Province")
  const stateTypeId = findLocationTypeId(locationTypes, "state", "province");
  const cityTypeId = findLocationTypeId(locationTypes, "city", "district");

  const countries = filterCountries(locations, locationTypes);
  const countryLocations = useMemo(
    () => filterCitiesForParent(locations, locationTypes, formData.country_id),
    [locations, locationTypes, formData.country_id]
  );
  const states = locations.filter(
    (l) =>
      String(l.location_type_id) === String(stateTypeId) &&
      String(l.parent_location_id) === String(formData.country_id)
  );
  const cityParentId = formData.state_id || formData.country_id;
  const cities = locations.filter(
    (l) =>
      String(l.location_type_id) === String(cityTypeId) &&
      cityParentId &&
      String(l.parent_location_id) === String(cityParentId)
  );

  const selectedAccrIds = formData.accreditation_ids
    ? formData.accreditation_ids.split(",").map((s) => s.trim())
    : [];

  const selectedDeptIds = formData.department_ids
    ? formData.department_ids.split(",").map((s) => s.trim())
    : [];

  const selectedSpecIds = formData.specialization_type_ids
    ? formData.specialization_type_ids.split(",").map((s) => s.trim())
    : [];

  const handleAccrToggle = (id) => {
    const strId = String(id);
    const updated = selectedAccrIds.includes(strId)
      ? selectedAccrIds.filter((x) => x !== strId)
      : [...selectedAccrIds, strId];
    onChange("accreditation_ids", updated.join(","));
  };

  const handleDeptToggle = (id) => {
    const strId = String(id);
    const updated = selectedDeptIds.includes(strId)
      ? selectedDeptIds.filter((x) => x !== strId)
      : [...selectedDeptIds, strId];
    onChange("department_ids", updated.join(","));
  };

  const handleSpecToggle = (id) => {
    const strId = String(id);
    const updated = selectedSpecIds.includes(strId)
      ? selectedSpecIds.filter((x) => x !== strId)
      : [...selectedSpecIds, strId];
    onChange("specialization_type_ids", updated.join(","));
  };

  const allDeptsSelected =
    departments.length > 0 &&
    departments.every((d) => selectedDeptIds.includes(String(d.department_id)));

  const allSpecsSelected =
    specializationTypes.length > 0 &&
    specializationTypes.every((st) => selectedSpecIds.includes(String(st.specialization_type_id)));

  const handleDeptSelectAll = () => {
    onChange(
      "department_ids",
      allDeptsSelected ? "" : departments.map((d) => d.department_id).join(",")
    );
  };

  const handleSpecSelectAll = () => {
    onChange(
      "specialization_type_ids",
      allSpecsSelected
        ? ""
        : specializationTypes.map((st) => st.specialization_type_id).join(",")
    );
  };

  const basicLocked = lockBasicInfo;
  const subTypeOptions = filterEstablishmentSubTypes(establishmentSubTypes, formData.establishment_type_id);

  const handleEstablishmentTypeChange = (value) => {
    onChange("establishment_type_id", value);
    onChange("establishment_sub_type_id", "");
  };

  const handleCountryChange = (countryId) => {
    onChange("country_id", countryId);
    onChange("location_id", "");
    onChange("location_name", "");
    onChange("state_id", "");
    onChange("city_id", "");
    const country = countries.find((c) => String(c.location_id) === String(countryId));
    onChange("country_name", country?.location_name || "");
    if (country?.country_code) onChange("country_code", country.country_code);
  };

  const handleLocationChange = (locationId) => {
    onChange("location_id", locationId);
    const loc = countryLocations.find((l) => String(l.location_id) === String(locationId));
    onChange("location_name", loc?.location_name || "");
  };

  return (
    <div className="em-form">
      {/* 1. Basic Information */}
      <EstSectionCard title="Basic Information" sectionNumber={1}>
        {basicLocked ? (
          <>
            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estTypeId">
                <Form.Label>Establishment Type <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  readOnly
                  className="em-field-locked"
                  value={formData.establishment_type_display || "—"}
                />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="establishmentSubTypeId">
                <Form.Label>Establishment Sub Type</Form.Label>
                <Form.Control
                  readOnly
                  className="em-field-locked"
                  value={formData.establishment_sub_type_display || "—"}
                />
              </Form.Group>
            </Row>
            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="brandName">
                <Form.Label>Brand Name <span className="text-danger">*</span></Form.Label>
                <Form.Control readOnly className="em-field-locked" value={formData.establishment_name || "—"} />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="legalEntityName">
                <Form.Label>Legal Entity Name</Form.Label>
                <Form.Control readOnly className="em-field-locked" value={formData.legal_entity_name || "—"} />
              </Form.Group>
            </Row>
            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estYear">
                <Form.Label>Establishment Year</Form.Label>
                <Form.Control readOnly className="em-field-locked" value={formData.establishment_year || "—"} />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="parentEstId">
                <Form.Label>Parent Entity</Form.Label>
                <Form.Control
                  readOnly
                  className="em-field-locked"
                  value={formData.parent_establishment_display || "None (Top-level)"}
                />
              </Form.Group>
            </Row>
            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estCountry">
                <Form.Label>Country</Form.Label>
                <Form.Control readOnly className="em-field-locked" value={formData.country_name || "—"} />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="estLocation">
                <Form.Label>Location</Form.Label>
                <Form.Control readOnly className="em-field-locked" value={formData.location_name || "—"} />
              </Form.Group>
            </Row>
            <Row className="em-row em-row--last">
              <Form.Group as={Col} md="6" controlId="estPayoutPct">
                <Form.Label>Payout %</Form.Label>
                <Form.Control
                  readOnly
                  className="em-field-locked"
                  value={formData.payout_percentage != null && formData.payout_percentage !== "" ? `${formData.payout_percentage}%` : "—"}
                />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="estEngagementLetter">
                <Form.Label>Engagement Letter</Form.Label>
                {formData.engagement_letter_path ? (
                  <div>
                    <a
                      href={`${UPLOADS_BASE}/uploads/${formData.engagement_letter_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View engagement letter
                    </a>
                  </div>
                ) : (
                  <Form.Control readOnly className="em-field-locked" value="—" />
                )}
              </Form.Group>
            </Row>
          </>
        ) : (
          <>
            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estTypeId">
                <Form.Label>Establishment Type <span className="text-danger">*</span></Form.Label>
                <Form.Select
                  required
                  value={formData.establishment_type_id || ""}
                  onChange={(e) => handleEstablishmentTypeChange(e.target.value)}
                >
                  <option value="">Select establishment type</option>
                  {normalizeEstablishmentTypes(estTypes).map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="establishmentSubTypeId">
                <Form.Label>Establishment Sub Type</Form.Label>
                <Form.Select
                  value={formData.establishment_sub_type_id || ""}
                  onChange={(e) => onChange("establishment_sub_type_id", e.target.value)}
                >
                  <option value="">Select establishment sub type</option>
                  {subTypeOptions.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                  {subTypeOptions.length === 0 && (
                    <option value="" disabled>Add sub types in Establishment Sub Type Master first</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Row>

            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="brandName">
                <Form.Label>Brand Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  required
                  type="text"
                  placeholder="e.g. Apollo Hospitals"
                  value={formData.establishment_name || ""}
                  onChange={(e) => onChange("establishment_name", e.target.value)}
                />
                <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="legalEntityName">
                <Form.Label>Legal Entity Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Registered legal entity name"
                  value={formData.legal_entity_name || ""}
                  onChange={(e) => onChange("legal_entity_name", e.target.value)}
                />
              </Form.Group>
            </Row>

            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estYear">
                <Form.Label>Establishment Year</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g. 1995"
                  min="1800"
                  max={new Date().getFullYear()}
                  value={formData.establishment_year || ""}
                  onChange={(e) => onChange("establishment_year", e.target.value)}
                />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="parentEstId">
                <Form.Label>Parent Entity</Form.Label>
                <Form.Select
                  value={formData.parent_establishment_id || ""}
                  onChange={(e) => onChange("parent_establishment_id", e.target.value)}
                >
                  <option value="">None (Top-level)</option>
                  {establishments.map((est) => (
                    <option key={est.establishment_id} value={est.establishment_id}>
                      {est.establishment_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>

            <Row className="em-row">
              <Form.Group as={Col} md="6" controlId="estCountry">
                <Form.Label>Country</Form.Label>
                {lockLocationFields ? (
                  <Form.Control readOnly className="em-field-locked" value={formData.country_name || "—"} />
                ) : (
                  <Form.Select
                    value={formData.country_id || ""}
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    <option value="">Select country</option>
                    {countries.map((c) => (
                      <option key={c.location_id} value={c.location_id}>{c.location_name}</option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="estLocation">
                <Form.Label>Location</Form.Label>
                {lockLocationFields ? (
                  <Form.Control readOnly className="em-field-locked" value={formData.location_name || "—"} />
                ) : (
                  <Form.Select
                    value={formData.location_id || ""}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    disabled={!formData.country_id}
                  >
                    <option value="">
                      {!formData.country_id ? "Select country first" : "Select location"}
                    </option>
                    {countryLocations.map((loc) => (
                      <option key={loc.location_id} value={loc.location_id}>{loc.location_name}</option>
                    ))}
                  </Form.Select>
                )}
                <span className="em-field-hint">City/District locations from Location Master for selected country</span>
              </Form.Group>
            </Row>

            <Row className="em-row em-row--last">
              <Form.Group as={Col} md="6" controlId="estPayoutPct">
                <Form.Label>Payout %</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="e.g. 15"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.payout_percentage ?? ""}
                  onChange={(e) => onChange("payout_percentage", e.target.value)}
                />
              </Form.Group>
              <Form.Group as={Col} md="6" controlId="estEngagementLetter">
                <Form.Label>Upload Engagement Letter</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,image/*"
                  className="em-file-input"
                  onChange={(e) => onChange("engagement_letter", e.target.files[0])}
                />
                {(formData.engagement_letter instanceof File || formData.engagement_letter_path) && (
                  <span className="em-file-status">
                    {formData.engagement_letter instanceof File
                      ? "Engagement letter selected ✓"
                      : (
                        <a
                          href={`${UPLOADS_BASE}/uploads/${formData.engagement_letter_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View uploaded letter
                        </a>
                      )}
                  </span>
                )}
              </Form.Group>
            </Row>
          </>
        )}
      </EstSectionCard>

      {!basicOnly && (
      <>
      {/* 2. Registration / Accreditation Details */}
      <EstSectionCard title="Registration / Accreditation Details" sectionNumber={2}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="regYear">
            <Form.Label>Registration Year</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 2010"
              min="1800"
              max={new Date().getFullYear()}
              value={formData.registration_year || ""}
              onChange={(e) => onChange("registration_year", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="regBody">
            <Form.Label>Registration Body</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. NMC, State Health Authority"
              value={formData.registration_body || ""}
              onChange={(e) => onChange("registration_body", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="regCertificate">
            <Form.Label>Registration Certificate (Upload)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,image/*"
              className="em-file-input"
              onChange={(e) => onChange("registration_certificate", e.target.files[0])}
            />
            {formData.registration_certificate instanceof File && (
              <span className="em-file-status">Certificate selected ✓</span>
            )}
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="accredIds">
            <Form.Label>Accreditation</Form.Label>
            <div className="em-accr-box">
              {accreditations.length === 0 && (
                <small className="text-muted">No accreditations available</small>
              )}
              {accreditations.map((a) => (
                <Form.Check
                  key={a.accreditation_id}
                  type="checkbox"
                  id={`accr-${a.accreditation_id}`}
                  label={a.accreditation_title}
                  checked={selectedAccrIds.includes(String(a.accreditation_id))}
                  onChange={() => handleAccrToggle(a.accreditation_id)}
                />
              ))}
            </div>
            <span className="em-field-hint">Multi-select — e.g. NABH, JCI</span>
          </Form.Group>
        </Row>
      </EstSectionCard>

      {/* 3. Contact Details */}
      <EstSectionCard title="Contact Details" sectionNumber={3}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="countryCode">
            <Form.Label>Country Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="+91"
              maxLength={10}
              value={formData.country_code || ""}
              onChange={(e) => onChange("country_code", e.target.value)}
              readOnly={!!formData.country_id}
            />
            <span className="em-field-hint">Auto-selected from Location Master</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="phoneNumbers">
            <Form.Label>Phone No</Form.Label>
            <EstPhoneList formData={formData} onChange={onChange} />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="addressLine1">
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              placeholder="Street, building, area"
              value={formData.address || ""}
              onChange={(e) => onChange("address", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="addressLine2">
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              placeholder="Landmark, suite, floor"
              value={formData.address_line_2 || ""}
              onChange={(e) => onChange("address_line_2", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="estState">
            <Form.Label>State / Province</Form.Label>
            {lockLocationFields ? (
              <Form.Control readOnly className="em-field-locked" value={formData.state_name || "—"} />
            ) : (
              <Form.Select
                value={formData.state_id || ""}
                onChange={(e) => {
                  onChange("state_id", e.target.value);
                  onChange("city_id", "");
                }}
                disabled={!formData.country_id}
              >
                <option value="">Select state</option>
                {states.map((s) => (
                  <option key={s.location_id} value={s.location_id}>{s.location_name}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="estCity">
            <Form.Label>City</Form.Label>
            {lockLocationFields ? (
              <Form.Control readOnly className="em-field-locked" value={formData.city_name || "—"} />
            ) : (
              <Form.Select
                value={formData.city_id || ""}
                onChange={(e) => onChange("city_id", e.target.value)}
                disabled={!formData.country_id}
              >
                <option value="">Select city</option>
                {cities.map((c) => (
                  <option key={c.location_id} value={c.location_id}>{c.location_name}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="longitude">
            <Form.Label>Longitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              placeholder="e.g. 77.2090"
              value={formData.longitude || ""}
              onChange={(e) => onChange("longitude", e.target.value)}
            />
            <span className="em-field-hint">Auto-select from Google map service</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="latitude">
            <Form.Label>Latitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              placeholder="e.g. 28.6139"
              value={formData.latitude || ""}
              onChange={(e) => onChange("latitude", e.target.value)}
            />
            <span className="em-field-hint">Auto-select from Google map service</span>
          </Form.Group>
        </Row>

        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="emailAddr">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="contact@hospital.com"
              value={formData.email_address || ""}
              onChange={(e) => onChange("email_address", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="website">
            <Form.Label>Website Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com"
              value={formData.website_address || ""}
              onChange={(e) => onChange("website_address", e.target.value)}
            />
          </Form.Group>
        </Row>
      </EstSectionCard>

      {/* 4. Hospital Profile */}
      <EstSectionCard title="Hospital Profile" sectionNumber={4}>
        <Row className="em-row">
          <Form.Group as={Col} md="12" controlId="shortProfile">
            <Form.Label>Short Profile</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Brief description for listings and previews"
              value={formData.short_profile || ""}
              onChange={(e) => onChange("short_profile", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="12" controlId="longProfile">
            <Form.Label>Long Profile</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Detailed hospital profile, history, and services"
              value={formData.long_profile || ""}
              onChange={(e) => onChange("long_profile", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="logoUpload">
            <Form.Label>Upload Logo</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              className="em-file-input"
              onChange={(e) => onChange("logo", e.target.files[0])}
            />
            {(formData.logo instanceof File || formData.logo_path) && (
              <div className="em-file-preview">
                <img
                  src={
                    formData.logo instanceof File
                      ? URL.createObjectURL(formData.logo)
                      : `${UPLOADS_BASE}/${formData.logo_path}`
                  }
                  alt="Logo preview"
                />
              </div>
            )}
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="profilePdf">
            <Form.Label>Profile Upload (PDF)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              className="em-file-input"
              onChange={(e) => onChange("profile_pdf", e.target.files[0])}
            />
            {(formData.profile_pdf instanceof File || formData.profile_pdf_path) && (
              <span className="em-file-status">PDF uploaded ✓</span>
            )}
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="12" controlId="virtualTour">
            <Form.Label>Virtual Tour (YouTube Link)</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://youtube.com/watch?v=..."
              value={formData.virtual_tour_url || ""}
              onChange={(e) => onChange("virtual_tour_url", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row em-row--paired-selects">
          <Form.Group as={Col} md="6" controlId="departments">
            <Form.Label>Departments</Form.Label>
            <div className="em-accr-box">
              {departments.length > 0 && (
                <Form.Check
                  type="checkbox"
                  id="dept-select-all"
                  className="em-select-all"
                  label="Select All"
                  checked={allDeptsSelected}
                  onChange={handleDeptSelectAll}
                />
              )}
              {departments.length === 0 && (
                <small className="text-muted">No departments available</small>
              )}
              {departments.map((d) => (
                <Form.Check
                  key={d.department_id}
                  type="checkbox"
                  id={`dept-${d.department_id}`}
                  label={d.department_name}
                  checked={selectedDeptIds.includes(String(d.department_id))}
                  onChange={() => handleDeptToggle(d.department_id)}
                />
              ))}
            </div>
            <span className="em-field-hint">From Department Master</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="specializations">
            <Form.Label>Specialization</Form.Label>
            <div className="em-accr-box">
              {specializationTypes.length > 0 && (
                <Form.Check
                  type="checkbox"
                  id="spec-select-all"
                  className="em-select-all"
                  label="Select All"
                  checked={allSpecsSelected}
                  onChange={handleSpecSelectAll}
                />
              )}
              {specializationTypes.length === 0 && (
                <small className="text-muted">No specializations available</small>
              )}
              {specializationTypes.map((st) => (
                <Form.Check
                  key={st.specialization_type_id}
                  type="checkbox"
                  id={`spec-${st.specialization_type_id}`}
                  label={st.specialization_type}
                  checked={selectedSpecIds.includes(String(st.specialization_type_id))}
                  onChange={() => handleSpecToggle(st.specialization_type_id)}
                />
              ))}
            </div>
            <span className="em-field-hint">From Specialization Type Master</span>
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="totalBeds">
            <Form.Label>No of Beds</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.total_beds || ""}
              onChange={(e) => onChange("total_beds", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="totalOTs">
            <Form.Label>No of OTs</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.total_ots || ""}
              onChange={(e) => onChange("total_ots", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="totalDoctors">
            <Form.Label>No of Doctors</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.total_doctors || ""}
              onChange={(e) => onChange("total_doctors", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="icuBeds">
            <Form.Label>No of ICU Beds</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.total_critical_care_beds || ""}
              onChange={(e) => onChange("total_critical_care_beds", e.target.value)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="emergencyDept">
            <Form.Label>Emergency / Trauma</Form.Label>
            <YesNoRadioInline
              name="emergencyDept"
              value={formData.emergency_dept_available}
              onChange={(v) => onChange("emergency_dept_available", v)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="bloodBank">
            <Form.Label>Blood Bank</Form.Label>
            <YesNoRadioInline
              name="bloodBank"
              value={formData.blood_bank_available ?? formData.ambulance_available}
              onChange={(v) => onChange("blood_bank_available", v)}
            />
          </Form.Group>
        </Row>

        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="nicuAvailable">
            <Form.Label>NICU Available</Form.Label>
            <YesNoRadioInline
              name="nicuAvailable"
              value={formData.nicu_available}
              onChange={(v) => onChange("nicu_available", v)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="picuAvailable">
            <Form.Label>PICU Available</Form.Label>
            <YesNoRadioInline
              name="picuAvailable"
              value={formData.picu_available}
              onChange={(v) => onChange("picu_available", v)}
            />
          </Form.Group>
        </Row>
      </EstSectionCard>

      {/* 5. Social Media Profile */}
      <EstSectionCard title="Social Media Profile" sectionNumber={5}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="facebook">
            <Form.Label>Facebook</Form.Label>
            <Form.Control
              type="text"
              placeholder="Facebook URL"
              value={formData.facebook_page || ""}
              onChange={(e) => onChange("facebook_page", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="youtube">
            <Form.Label>YouTube Channel</Form.Label>
            <Form.Control
              type="text"
              placeholder="YouTube URL"
              value={formData.youtube_channel || ""}
              onChange={(e) => onChange("youtube_channel", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="instagram">
            <Form.Label>Instagram</Form.Label>
            <Form.Control
              type="text"
              placeholder="Instagram URL"
              value={formData.instagram || ""}
              onChange={(e) => onChange("instagram", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="linkedin">
            <Form.Label>LinkedIn</Form.Label>
            <Form.Control
              type="text"
              placeholder="LinkedIn URL"
              value={formData.linkedin || ""}
              onChange={(e) => onChange("linkedin", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="xProfile">
            <Form.Label>X / Twitter</Form.Label>
            <Form.Control
              type="text"
              placeholder="X (Twitter) URL"
              value={formData.x_profile || ""}
              onChange={(e) => onChange("x_profile", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="telegram">
            <Form.Label>Telegram Channel</Form.Label>
            <Form.Control
              type="text"
              placeholder="Telegram URL"
              value={formData.telegram_channel || ""}
              onChange={(e) => onChange("telegram_channel", e.target.value)}
            />
          </Form.Group>
        </Row>
      </EstSectionCard>
      </>
      )}
    </div>
  );
};

export default EstablishmentProfileForm;
